import { useState, useMemo, useEffect, useRef, startTransition } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  PublicUserOrganization,
  UserOrganization,
} from "../../__generated__/graphql";
import useOrganizationMembers from "../../hooks/useOrganizationMembers";
import useOrganizationSharings from "../../hooks/useOrganizationSharings";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import Avatar from "../Avatar";
import Checkbox from "../Checkbox";
import { cn } from "../../lib/utils";
import { ChevronUpDownIcon, UserGroupIcon } from "@heroicons/react/24/outline";

// Helper function to normalize UserOrganization to PublicUserOrganization-like structure
const normalizeUserOrganization = (
  userOrg: UserOrganization | PublicUserOrganization
): PublicUserOrganization => {
  // If it's already a PublicUserOrganization, return as is
  if (
    "__typename" in userOrg &&
    userOrg.__typename === "PublicUserOrganization"
  ) {
    return userOrg;
  }

  // Convert UserOrganization to PublicUserOrganization format
  return {
    __typename: "PublicUserOrganization",
    id: userOrg.id,
    name: userOrg.name,
    organizationName: userOrg.organizationName,
    organizationId: userOrg.organizationId,
    organizationType: userOrg.organizationType,
    email: userOrg.email ?? null,
    phoneNumber: userOrg.phoneNumber ?? null,
    avatarBucketKey: userOrg.avatarBucketKey ?? null,
    jobTitle: userOrg.jobTitle ?? null,
    birthDate: userOrg.birthDate ?? null,
  };
};

interface UserSelectListProps {
  onSelect: (selectedUsers: PublicUserOrganization[]) => void;
  className?: string;
  // If provided, use this list instead of fetching org members
  participants?: PublicUserOrganization[];
}

export default function UserSelectList({
  onSelect,
  className,
  participants,
}: UserSelectListProps) {
  const { userOrganization: currentUserOrganization } = useUser();

  // Only fetch org members if participants are not provided
  const { data: membersData, loading: membersLoading } = useOrganizationMembers(
    { skip: !!participants }
  );
  const { externalUserOrganizations, loading: sharingsLoading } =
    useOrganizationSharings({ skip: !!participants });

  const loading = !participants && (membersLoading || sharingsLoading);

  // Combine and normalize all users (or use provided participants)
  const allUsers = useMemo(() => {
    // If participants are provided, use them directly
    if (participants) {
      return participants;
    }

    const members = (membersData?.organizationMembers ?? [])
      .filter((member): member is UserOrganization => member !== null)
      .map((member) => normalizeUserOrganization(member));

    const externalUsers = (externalUserOrganizations ?? []).filter(
      (user): user is PublicUserOrganization => user !== null
    );

    // Combine and deduplicate by id
    // Prioritize members data (organization members) over external users
    // to ensure consistent name formatting
    const userMap = new Map<string, PublicUserOrganization>();

    // First add external users (they might not be in members)
    externalUsers.forEach((user) => {
      if (!userMap.has(user.id)) {
        userMap.set(user.id, user);
      }
    });

    // Then add/overwrite with members data (prioritized source)
    members.forEach((user) => {
      userMap.set(user.id, user);
    });

    return Array.from(userMap.values());
  }, [membersData, externalUserOrganizations, participants]);

  // Sort users: current user first, then alphabetically by name
  const sortedUsers = useMemo(() => {
    const currentUserId = currentUserOrganization?.id;

    const sorted = [...allUsers].sort((a, b) => {
      // Current user always first
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;

      // Then sort alphabetically by name
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

    return sorted;
  }, [allUsers, currentUserOrganization?.id]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set()
  );
  const prevSelectedUserIdsRef = useRef<Set<string>>(new Set());

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return sortedUsers;

    const query = searchQuery.toLowerCase().trim();
    return sortedUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(query) ||
        user.organizationName.toLowerCase().includes(query) ||
        (user.email?.toLowerCase().includes(query) ?? false) ||
        (user.jobTitle?.toLowerCase().includes(query) ?? false)
    );
  }, [sortedUsers, searchQuery]);

  // Notify parent when selection changes
  useEffect(() => {
    // Only update if selection actually changed
    const prevIds = prevSelectedUserIdsRef.current;
    const hasChanged =
      prevIds.size !== selectedUserIds.size ||
      Array.from(selectedUserIds).some((id) => !prevIds.has(id)) ||
      Array.from(prevIds).some((id) => !selectedUserIds.has(id));

    if (hasChanged) {
      const selectedUsers = sortedUsers.filter((user) =>
        selectedUserIds.has(user.id)
      );
      // Use startTransition to make this update non-blocking
      startTransition(() => {
        onSelect(selectedUsers);
      });
      prevSelectedUserIdsRef.current = new Set(selectedUserIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserIds, sortedUsers]);

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className={cn("p-4 text-secondary text-sm", className)}>
        Chargement...
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="flex items-center justify-between text-sm text-primary cursor-pointer hover:text-secondary transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <UserGroupIcon className="w-4 h-4" />
          <div className="text-sm">Filtrer par utilisateur</div>
        </div>
        <ChevronUpDownIcon className="w-4 h-4" />
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="flex flex-col gap-2">
              {/* Search input */}
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 text-xs w-full p-2 rounded-sm bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border placeholder:text-tertiary"
              />

              {/* User list */}
              <div className="flex flex-col gap-1 max-h-96 overflow-y-auto custom-scrollbar">
                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-tertiary text-sm text-center">
                    Aucun utilisateur trouvé
                  </div>
                ) : (
                  filteredUsers.map((user) => {
                    const isSelected = selectedUserIds.has(user.id);
                    const isCurrentUser =
                      user.id === currentUserOrganization?.id;

                    return (
                      <div
                        key={user.id}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-md  cursor-pointer hover:bg-card/40 transition-colors select-none",
                          isCurrentUser && "bg-card/60"
                        )}
                        onClick={() => handleUserToggle(user.id)}
                      >
                        <Checkbox checked={isSelected} />
                        <Avatar userOrganization={user} size="small" />
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-sm text-primary truncate">
                              {user.name}
                            </span>
                            {isCurrentUser && (
                              <span className="text-xs text-secondary">
                                (Vous)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
